import React from "react";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import baseURL from "../../Url/NodeBaseURL";
import OrderInvoice from "./templates/OrderInvoice";
import {
  amountToWords,
  buildPartyBlock,
  calculatePaymentReceived,
  defaultCompanyInfo,
  formatCurrency,
  formatDate,
  isSameState,
  parseNumber,
} from "./utils/common";

const fetchInvoiceDetails = async (invoiceNumber) => {
  if (!invoiceNumber) {
    throw new Error("Invoice number is required to generate PDF");
  }

  const response = await axios.get(
    `${baseURL}/get-repair-details/${invoiceNumber}`
  );
  return response.data;
};

const fetchProducts = async () => {
  const response = await axios.get(`${baseURL}/get/products`);
  return Array.isArray(response.data) ? response.data : [];
};

const fetchUrdPurchases = async (estimateNumber) => {
  if (!estimateNumber) return { purchases: [], totalAmount: 0 };
  try {
    const response = await axios.get(`${baseURL}/get/olditems/${estimateNumber}`);
    if (!Array.isArray(response.data)) {
      return { purchases: [], totalAmount: 0 };
    }
    const totalAmount = response.data.reduce((sum, item) => {
      return sum + parseNumber(item.total_amount || 0);
    }, 0);
    return { purchases: response.data, totalAmount };
  } catch (error) {
    console.warn("URD purchase fetch failed:", error);
    return { purchases: [], totalAmount: 0 };
  }
};

const fetchSalesReturns = async (estimateNumber) => {
  if (!estimateNumber) return { returns: [], totalAmount: 0 };
  try {
    const response = await axios.get(`${baseURL}/get-estimate-sale-returns/${estimateNumber}`);
    if (!Array.isArray(response.data)) {
      return { returns: [], totalAmount: 0 };
    }
    const totalAmount = response.data.reduce((sum, item) => {
      const totalPrice = parseNumber(item.total_price) || 0;
      const taxAmt = parseNumber(item.tax_amt) || 0;
      const taxable = parseNumber(
        item.salesTaxableAmount ||
        item.sales_taxable_amt ||
        item.piece_taxable_amt ||
        item.taxable_amount ||
        0
      );
      const hm = parseNumber(item.hm_charges) || 0;
      const net = totalPrice > 0 ? totalPrice - hm : taxable + taxAmt - hm;
      return sum + net;
    }, 0);
    return { returns: response.data, totalAmount };
  } catch (error) {
    console.warn("Sales return fetch failed:", error);
    return { returns: [], totalAmount: 0 };
  }
};

const buildHsnMap = (products) =>
  products.reduce((map, product) => {
    if (product?.product_name) {
      map[product.product_name] = product.hsn_code || "";
    }
    if (product?.category) {
      map[product.category] = product.hsn_code || "";
    }
    return map;
  }, {});

const filterSalesLines = (invoiceData) =>
  (invoiceData?.repeatedData || []).filter((item) =>
    ["Sales", "ConvertedInvoice", "ConvertedRepairInvoice"].includes(
      item.transaction_status
    )
  );

const collectTotals = (lines) =>
  lines.reduce(
    (totals, item) => ({
      rateAmount: totals.rateAmount + parseNumber(item.rate_amt),
      makingCharges: totals.makingCharges + parseNumber(item.making_charges),
      stonePrice: totals.stonePrice + parseNumber(item.stone_price),
      hmCharges: totals.hmCharges + parseNumber(item.hm_charges),
      discount: totals.discount + parseNumber(
        item.discount_amount || 
        item.discount || 
        item.disscount || 
        0
      ),
      festivalOffer:totals.festivalOffer + parseNumber(item.festival_discount),
      taxAmount: totals.taxAmount + parseNumber(item.tax_amt),
    }),
    {
      rateAmount: 0,
      makingCharges: 0,
      stonePrice: 0,
      hmCharges: 0,
      discount: 0,
      festivalOffer:0,
      taxAmount: 0,
    }
  );

const deriveInvoiceInfo = (invoiceData, saleLines) => {
  if (invoiceData?.uniqueData) return invoiceData.uniqueData;
  if (invoiceData?.invoice) return invoiceData.invoice;
  if (saleLines?.length) return saleLines[0];
  return {};
};

const buildTaxRows = (taxAmount, subtotal, invoiceInfo) => {
  if (!taxAmount) return [];

  const percentFromData = parseNumber(invoiceInfo.tax_percent);
  const effectivePercent = subtotal
    ? ((taxAmount / subtotal) * 100).toFixed(2)
    : percentFromData || 0;

  const sameState = isSameState(invoiceInfo.state, invoiceInfo.state_code);

  if (sameState) {
    const halfAmount = taxAmount / 2;
    return [
      { label: "SGST", percent: parseNumber(effectivePercent) / 2 || 0, amount: formatCurrency(halfAmount) },
      { label: "CGST", percent: parseNumber(effectivePercent) / 2 || 0, amount: formatCurrency(halfAmount) },
    ];
  }

  return [
    {
      label: "IGST",
      percent: parseNumber(effectivePercent),
      amount: formatCurrency(taxAmount),
    },
  ];
};

const buildItemRows = (saleLines, hsnMap, defaultTaxPercent = 0) =>
  saleLines.map((item, idx) => {
    const descriptionParts = [
      item.metal_type || item.metal,
      item.product_name,
    ].filter(Boolean);
    let description = descriptionParts.join(" - ") || "Item";
    if (item.client_visibility === "Client Facing" && item.remarks) {
      description += ` (${item.remarks})`;
    }
    
    const rate =
      parseNumber(item.pieace_cost)||
      parseNumber(item.rate) ||
      parseNumber(item.rate_amt);
    const qty = parseNumber(item.qty, 1) || 1;
    // const amount =
    //   parseNumber(item.rate_amt) +
    //     parseNumber(item.making_charges) +
    //     parseNumber(item.stone_price)+
    //     parseNumber(item.hm_charges)  ||
    //   parseNumber(item.total_price);
    const amount =
    item.pricing === "By Weight"
      ? parseNumber(item.rate_amt) +
        parseNumber(item.making_charges) +
        parseNumber(item.stone_price) +
        parseNumber(item.hm_charges)
      : parseNumber(item.rate_amt) +
        parseNumber(item.making_charges) +
        parseNumber(item.stone_price);


    // Format MC display
    let mcDisplay = "-";
    if (item.mc_per_gram != null && item.mc_per_gram !== "") {
      const metalType = (item.metal_type || item.metal || "").toLowerCase();
      if (["gold", "diamond", "others"].includes(metalType)) {
        mcDisplay = `${parseFloat(item.mc_per_gram).toFixed(2)} %`;
      } else {
        mcDisplay = parseFloat(item.mc_per_gram).toFixed(2);
      }
    }

    // Determine status for sales
    let status = "Delivered";
    if (item.transaction_status === "ConvertedInvoice" || item.transaction_status === "ConvertedRepairInvoice") {
      status = "Delivered";
    } else if (item.transaction_status === "Sales") {
      status = "Delivered";
    }

    return {
      sno: idx + 1,
      description,
      hsn: hsnMap[item.product_name] || hsnMap[item.category] || "-",
      qty: qty.toFixed(2),
      purity: item.printing_purity || item.purity || "-",
      grossWeight: item.gross_weight ? parseFloat(item.gross_weight).toFixed(3) : "-",
      stoneWeight: item.stone_weight ? parseFloat(item.stone_weight).toFixed(3) : "-",
      netWeight: item.total_weight_av ? parseFloat(item.total_weight_av).toFixed(3) : "-",
      rate: formatCurrency(rate),
      vaPercent: item.va_percent || item.va_perc || item.wastage_percent ? parseFloat(item.va_percent || item.va_perc || item.wastage_percent).toFixed(2) : "-",
      mc: mcDisplay,
      stoneAmount: formatCurrency(parseNumber(item.stone_price)),
      status: status,
      amount: formatCurrency(amount),
    };
  });

const buildSalesDocument = ({ invoiceInfo, saleLines, hsnMap, totals, oldItemsAmount = 0, salesReturnAmount = 0, schemeAmount = 0 }) => {
  const taxableAmount =
    parseNumber(invoiceInfo.taxable_amount) ||
    totals.rateAmount + totals.makingCharges + totals.stonePrice + totals.hmCharges;
  const taxAmount =
    parseNumber(invoiceInfo.tax_amount) ||
    parseNumber(invoiceInfo.tax_amt) ||
    totals.taxAmount;
  //const festivalOffer=parseNumber(saleLines[0].festival_discount) || 0;
  const netAmount =
    parseNumber(invoiceInfo.net_amount) ||
    parseNumber(invoiceInfo.net_bill_amount) ||
    taxableAmount + taxAmount;
  const netPayableAmount =
    parseNumber(invoiceInfo.net_payable_amount) || netAmount;

  const paymentMade = calculatePaymentReceived(
    invoiceInfo.cash_amount,
    invoiceInfo.card_amt,
    invoiceInfo.chq_amt,
    invoiceInfo.online_amt,
    invoiceInfo.receipts_amt
  );

  // Calculate total discount from line items and invoice level
  const lineItemDiscount = totals.discount;
  const invoiceLevelDiscount = parseNumber(
    invoiceInfo.discount_amount || 
    invoiceInfo.discount || 
    invoiceInfo.disscount || 
    invoiceInfo.total_discount ||
    0
  );
  const totalDiscount = lineItemDiscount + invoiceLevelDiscount;
  
  // Debug logging
  console.log("Discount calculation:", {
    lineItemDiscount,
    invoiceLevelDiscount,
    totalDiscount,
    invoiceInfo: {
      discount_amount: invoiceInfo.discount_amount,
      discount: invoiceInfo.discount,
      disscount: invoiceInfo.disscount,
      total_discount: invoiceInfo.total_discount,
    },
    totals: totals.discount,
    paymentMade,
  });

  // Build tax rows
  const taxRows = buildTaxRows(taxAmount, taxableAmount, invoiceInfo);

  return (
    <OrderInvoice
      headerTitle="Tax Invoice"
      company={defaultCompanyInfo}
      invoiceMeta={{
        number: invoiceInfo.invoice_number || "N/A",
        invoiceDate: invoiceInfo.date,
        invoice:true,
        terms: invoiceInfo.terms || "Cash",
        dueDate: formatDate(invoiceInfo.due_date),
        placeOfSupply:
          invoiceInfo.place_of_supply ||
          invoiceInfo.state ||
          defaultCompanyInfo.placeOfSupply,
      }}
      billTo={buildPartyBlock(invoiceInfo)}
      items={buildItemRows(saleLines, hsnMap, invoiceInfo.tax_percent)}
      totals={{
        subtotal: formatCurrency(taxableAmount),
        //discount: totalDiscount > 0 ? formatCurrency(totalDiscount) : null,
        // festivalOffer: null,
        discount: totals.discount > 0 ? formatCurrency(totals.discount) : null,
        festivalOffer: totals.festivalOffer > 0 ? formatCurrency(totals.festivalOffer) : null,
        taxRows: taxRows,
        total: formatCurrency(netAmount),
        oldItemsAmount: oldItemsAmount > 0 ? formatCurrency(oldItemsAmount) : null,
        schemeAmount: schemeAmount > 0 ? formatCurrency(schemeAmount) : null,
        salesReturnAmount: salesReturnAmount > 0 ? formatCurrency(salesReturnAmount) : null,
        advanceAmount: null,
        netPayableAmount: formatCurrency(netPayableAmount-oldItemsAmount - schemeAmount - salesReturnAmount),
        paymentMade: paymentMade,
        balanceDue: formatCurrency(Math.max(netPayableAmount - paymentMade, 0)),
      }}
      amountInWords={amountToWords(netAmount)}
      notes={
        invoiceInfo.remarks ||
        invoiceInfo.note ||
        "Thanks for your business!"
      }
      signature="Authorized Signature"
    />
  );
};

export const generateSalesPdf = async (
  invoiceNumber,
  { openInNewTab = true, autoPrint = true } = {}
) => {
  const [invoiceData, products] = await Promise.all([
    fetchInvoiceDetails(invoiceNumber),
    fetchProducts(),
  ]);

  const saleLines = filterSalesLines(invoiceData);
  if (!saleLines.length) {
    throw new Error("No sale line items available to generate PDF");
  }

  const invoiceInfo = deriveInvoiceInfo(invoiceData, saleLines);
  const InvoiceNumber = invoiceInfo.invoice_number || invoiceInfo.estimate_number;

  // Fetch URD purchases and sales returns
  // const [{ totalAmount: oldItemsAmount }, { totalAmount: salesReturnAmount }] = await Promise.all([
  //   fetchUrdPurchases(InvoiceNumber),
  //   fetchSalesReturns(InvoiceNumber),
  // ]);

  // Get scheme amount from invoice info if available
  const schemeAmount = parseNumber(invoiceInfo.scheme_amount || invoiceInfo.schemeAmount || 0);
  const oldItemsAmount = parseNumber(invoiceInfo.old_exchange_amt || invoiceInfo.old_exchange_amt || 0);
  const salesReturnAmount = parseNumber(invoiceInfo.sale_return_amt || invoiceInfo.sale_return_amt || 0);

  const document = buildSalesDocument({
    invoiceInfo,
    saleLines,
    hsnMap: buildHsnMap(products),
    totals: collectTotals(saleLines),
    oldItemsAmount,
    salesReturnAmount,
    schemeAmount,
  });

  const blob = await pdf(document).toBlob();
  const fileName = `Invoice-${invoiceNumber}.pdf`;

  if (openInNewTab && typeof window !== "undefined") {
    const pdfUrl = URL.createObjectURL(blob);
    const newWindow = window.open(pdfUrl, "_blank");
    if (newWindow && autoPrint) {
      newWindow.onload = () => {
        newWindow.focus();
        newWindow.print();
      };
    }
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);
  }

  return { blob, fileName };
};

export default generateSalesPdf;

